import React from 'react';
import PropTypes from 'prop-types';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Card, CardContent, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { BLACK, PRIMARY_COLOR } from '../../theme/colorConstants';
import { PRIMARY_FONT } from '../../theme/fontConstants';
import DraggableCard from '../DraggableCard/DraggableCard';
import HeaderBar from '../ShiftCard/HeaderBar/HeaderBar';

const useStyles = makeStyles({
  card: {
    perspective: '1000px',
    width: '312px',
    height: '410px',
  },
  cardContent: {
    padding: '12px 12px 0 12px !important',
  },
  cardInner: {
    position: 'relative',
    width: '100%',
    height: '100%',
    textAlign: 'center',
    transition: 'transform 0.4s',
    transformStyle: 'preserve-3d',
  },
  cardFlipped: {
    transform: 'rotateY(180deg)',
  },
  cardFrontBack: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    backfaceVisibility: 'hidden',
    top: 0,
    left: 0,
  },
  cardFront: {
    zIndex: 2,
    transform: 'rotateY(0deg)',
  },
  cardBack: {
    transform: 'rotateY(180deg)',
  },
  expandBox: {
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '35px',
    backgroundColor: '#F3F3F3',
    position: 'fixed',
    bottom: '0px',
    right: '0px',
  },
});

const FlipCard = ({
  frontContent,
  backContent,
  isExpanded,
  setIsExpanded,
  index,
  date,
  moveCard,
  isFlipped,
  handleFlipClick,
  isCancelled,
  timeStatus,
  showSelection,
  checked,
  disabled,
  onToggle,
  renderHeader,
  cardWidth = 312,
  cardHeight = 410,
  expandedHeight = 820,
  selectedBorder = false,
  footerAction = null,
  collapseLabel = '',
  borderRadius = '8px',
  redesign = false,
}) => {
  const classes = useStyles();
  const shrinkText = LanguageConverterV2('roster.shrink');
  const expandText = LanguageConverterV2('roster.expand');
  const collapseOffset = redesign ? '36px' : '35px';
  let CollapseIcon = ArrowDropDownIcon;
  if (redesign && isExpanded) {
    CollapseIcon = KeyboardArrowUpIcon;
  } else if (redesign) {
    CollapseIcon = KeyboardArrowDownIcon;
  } else if (isExpanded) {
    CollapseIcon = ArrowDropUpIcon;
  }

  return (
    <DraggableCard index={index} date={date} moveCard={moveCard}>
      {({ drag }) => (
        <Box
          sx={{
            perspective: '1000px',
            width: cardWidth,
            height: isExpanded && isFlipped ? expandedHeight : cardHeight,
          }}
        >
          <Box className={`${classes.cardInner} ${isFlipped && classes.cardFlipped}`}>
            <Card
              className={`${classes.cardFrontBack} ${
                isFlipped ? classes.cardBack : classes.cardFront
              }`}
              style={{
                overflow: 'auto',
                borderRadius,
                boxShadow: '0px 2px 6px 0px rgba(0, 0, 0, 0.25)',
                outline: selectedBorder && checked ? `2px solid ${PRIMARY_COLOR[70]}` : 'none',
                outlineOffset: '-2px',
              }}
            >
              {typeof renderHeader === 'function' ? (
                renderHeader({ isFlipped })
              ) : (
                <HeaderBar
                  onFlipClick={handleFlipClick}
                  isFlipped={isFlipped}
                  onDrag={drag}
                  timeStatus={timeStatus}
                  isCancelled={isCancelled}
                  showSelection={showSelection}
                  checked={checked}
                  disabled={disabled}
                  onToggle={onToggle}
                />
              )}
              <CardContent
                className={classes.cardContent}
                sx={
                  footerAction
                    ? { paddingBottom: isFlipped ? '124px !important' : '88px !important' }
                    : undefined
                }
              >
                {isFlipped ? backContent : frontContent}
              </CardContent>
              {footerAction ? (
                <Box
                  sx={{
                    position: 'fixed',
                    left: 0,
                    right: 0,
                    bottom: isFlipped ? collapseOffset : 0,
                    px: '24px',
                    pt: 1,
                    pb: '24px',
                    backgroundColor: '#fff',
                    zIndex: 2,
                    borderBottomLeftRadius: isFlipped ? 0 : borderRadius,
                    borderBottomRightRadius: isFlipped ? 0 : borderRadius,
                  }}
                >
                  {footerAction}
                </Box>
              ) : null}
              {isFlipped && (
                <Grid
                  className={classes.expandBox}
                  container
                  onClick={() => setIsExpanded(!isExpanded)}
                  sx={{
                    borderBottomLeftRadius: borderRadius,
                    borderBottomRightRadius: borderRadius,
                    ...(redesign && {
                      '&&': {
                        backgroundColor: BLACK[20],
                        height: '36px',
                      },
                    }),
                  }}
                >
                  <Typography
                    sx={
                      redesign
                        ? {
                            '&&': {
                              fontFamily: PRIMARY_FONT[700],
                              fontSize: '14px',
                              lineHeight: '17px',
                              color: PRIMARY_COLOR[70],
                              textTransform: 'none',
                            },
                          }
                        : { textTransform: isExpanded && collapseLabel ? 'none' : 'uppercase' }
                    }
                  >
                    {isExpanded ? collapseLabel || shrinkText : expandText}
                  </Typography>
                  <CollapseIcon
                    sx={redesign ? { fontSize: 20, color: PRIMARY_COLOR[70] } : undefined}
                  />
                </Grid>
              )}
            </Card>
          </Box>
        </Box>
      )}
    </DraggableCard>
  );
};

FlipCard.propTypes = {
  frontContent: PropTypes.node,
  backContent: PropTypes.node,
  index: PropTypes.number.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  setIsExpanded: PropTypes.func.isRequired,
  date: PropTypes.string.isRequired,
  moveCard: PropTypes.func.isRequired,
  isFlipped: PropTypes.bool.isRequired,
  handleFlipClick: PropTypes.func.isRequired,
  isCancelled: PropTypes.bool.isRequired,
  timeStatus: PropTypes.string.isRequired,
  showSelection: PropTypes.bool,
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  onToggle: PropTypes.func,
  renderHeader: PropTypes.func,
  cardWidth: PropTypes.number,
  cardHeight: PropTypes.number,
  expandedHeight: PropTypes.number,
  selectedBorder: PropTypes.bool,
  footerAction: PropTypes.node,
  collapseLabel: PropTypes.string,
  borderRadius: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  redesign: PropTypes.bool,
};

export default FlipCard;
